import type { PanchangTermName } from "@vedaunion/shared";
import { formatInTz } from "../../lib/tz";

/**
 * Shared rendering helpers for the Vedic calendar surfaces. A term carries an
 * IAST name and (where mapped) a Devanāgarī rendering; we show IAST as the
 * primary label with Devanāgarī as a quiet companion.
 */

export function termLabel(t: PanchangTermName | null | undefined): string {
  if (!t) return "";
  return t.iast || t.en || "";
}

/** Render a term as "Iast देव" with the Devanāgarī muted. */
export function Term({
  value,
  className = "",
}: {
  value: PanchangTermName | null | undefined;
  className?: string;
}) {
  if (!value) return null;
  const primary = value.iast || value.en;
  return (
    <span className={className}>
      {primary}
      {value.deva ? <span className="pa-deva"> {value.deva}</span> : null}
    </span>
  );
}

/** HH:mm in the panchang timezone. */
export function fmtTime(iso: string | null | undefined, tz: string): string {
  if (!iso) return "—";
  return formatInTz(iso, tz, { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** A short weekday + time, e.g. "Tue · 14:55". */
export function fmtDayTime(iso: string | null | undefined, tz: string): string {
  if (!iso) return "—";
  return formatInTz(iso, tz, {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export interface Interval {
  start: string;
  end: string;
}

/** True when `now` is within [start, end). */
export function isActive(iv: Interval, now: Date): boolean {
  const s = new Date(iv.start).getTime();
  const e = new Date(iv.end).getTime();
  return now.getTime() >= s && now.getTime() < e;
}

/** The first segment of any period that contains `now`. */
export function activeIn<T extends { segments: Interval[] }>(
  periods: T[],
  now: Date,
): T | null {
  for (const p of periods) {
    if (p.segments.some((s) => isActive(s, now))) return p;
  }
  return null;
}

/** Normalize a choghadiya quality string to a semantic class. */
export function qualityClass(type: string): "good" | "bad" | "neutral" {
  const t = type.toLowerCase();
  if (t.includes("auspicious") && !t.includes("in")) return "good";
  if (t === "good") return "good";
  if (t.includes("inauspicious")) return "bad";
  return "neutral";
}
